import { readRawCliContext } from "@/hooks/useCliContext";
import { EXTENSION_QUICKSTART_PATH, WORKSPACE_QUICKSTART_PATH } from "./extension/constants";

/**
 * Which Quick Start a user sees depends on where they entered onboarding:
 *
 * - `?from=workspaces` (workspace creation) → the Altimate Code page;
 * - `?from=power-user` (Power User for dbt onboarding) → the Power User for dbt page;
 * - no marker, or an unknown one (anywhere else in SaaS) → the Power User for dbt page.
 *
 * A sign-in or sign-up opened by the Altimate Code CLI (it carries
 * `cli_context`, in the fragment or, from older builds, the query) counts as a
 * workspace entry unless the URL names one explicitly.
 *
 * The marker usually arrives on /login or /register, so it is kept until the
 * post-sign-in redirect lands on /quickstart. localStorage is per origin, so
 * the hand-offs from the public domain to the instance subdomain (instance
 * finder, Google callback) carry it in the URL. localStorage, not
 * sessionStorage: email sign-up finishes in the verification link's new tab.
 * It is cleared when /quickstart reads it, and ignored after a day, so it
 * never decides a much later visit.
 */
export const QUICKSTART_ENTRY_PARAM = "from";

export type QuickstartEntry = "workspaces" | "power-user";

const STORAGE_KEY = "altimate.quickstartEntry";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const parseQuickstartEntry = (value: string | null): QuickstartEntry | undefined => {
  if (value === "workspaces" || value === "power-user") {
    return value;
  }
  return undefined;
};

export const quickstartPathFor = (entry: QuickstartEntry | undefined): string =>
  entry === "workspaces" ? WORKSPACE_QUICKSTART_PATH : EXTENSION_QUICKSTART_PATH;

const hasCliContext = (search: string, hash: string): boolean => {
  const raw = readRawCliContext(search, hash);
  return raw !== null && raw !== "";
};

/**
 * The entry a sign-in or sign-up URL implies: its marker, else workspaces for
 * the CLI. A marker that is present but unknown still decides (no entry).
 */
export const entryFromUrl = (search: string, hash = ""): QuickstartEntry | undefined => {
  const marker = new URLSearchParams(search).get(QUICKSTART_ENTRY_PARAM);
  if (marker !== null) {
    return parseQuickstartEntry(marker);
  }
  return hasCliContext(search, hash) ? "workspaces" : undefined;
};

/** Where sign-up was entered from, for the sign-up rows: the URL's marker, or the CLI when
 * the URL carries its context; nothing when neither says. */
export type SignupEntryPoint = "power-user" | "workspace" | "cli";
export const signupEntryPoint = ({
  search,
  hash,
}: {
  search: string;
  hash: string;
}): SignupEntryPoint | undefined => {
  if (hasCliContext(search, hash)) return "cli";
  const marker = parseQuickstartEntry(new URLSearchParams(search).get(QUICKSTART_ENTRY_PARAM));
  if (marker === "power-user") return "power-user";
  if (marker === "workspaces") return "workspace";
  return undefined;
};

/** `&from=<entry>` to append to a hand-off URL's query, or "" without an entry. */
export const quickstartEntryQuery = (entry: QuickstartEntry | undefined): string =>
  entry === undefined ? "" : `&${QUICKSTART_ENTRY_PARAM}=${entry}`;

/** Keep the entry a URL implies for the redirect after sign-in. */
export const stashQuickstartEntry = (search: string, hash = ""): void => {
  const entry = entryFromUrl(search, hash);
  if (entry === undefined) {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ entry, at: Date.now() }));
  } catch {
    // Private mode or storage full: the marker is lost and the default page shows.
  }
};

export const peekQuickstartEntry = (now = Date.now()): QuickstartEntry | undefined => {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null");
    if (typeof stored !== "object" || stored === null) {
      return undefined;
    }
    const { entry, at } = stored as { entry?: unknown; at?: unknown };
    if (typeof at !== "number" || now - at > MAX_AGE_MS) {
      return undefined;
    }
    return parseQuickstartEntry(typeof entry === "string" ? entry : null);
  } catch {
    return undefined;
  }
};

export const clearQuickstartEntry = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: nothing was stored.
  }
};
